/**
 * Editor Mode Detection and Communication
 *
 * Handles postMessage-based communication between the tracker script (running in customer's site iframe)
 * and the parent EventFoundry editor application.
 */

import {
  createEditorOverlay,
  enablePreviewOverlay,
  disablePreviewOverlay,
  enterParamCaptureMode,
  exitParamCaptureMode
} from './editor-overlay.js';
import { getParentOrigin, setParentOrigin } from './parent-origin.js';

/**
 * Callback function for reloading event definitions
 * Set during initialization
 */
let reloadCallback = null;
let previewLocationTrackingInstalled = false;

function getCurrentPreviewUrl() {
  return window.location.href;
}

function postPreviewLocation() {
  const targetOrigin = getParentOrigin();
  if (!targetOrigin) {
    return;
  }

  window.parent.postMessage({
    type: 'PREVIEW_LOCATION',
    payload: {
      url: getCurrentPreviewUrl()
    }
  }, targetOrigin);
}

function installPreviewLocationTracking() {
  if (previewLocationTrackingInstalled) {
    return;
  }

  previewLocationTrackingInstalled = true;
  postPreviewLocation();

  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = function(...args) {
    originalPushState(...args);
    postPreviewLocation();
  };

  window.history.replaceState = function(...args) {
    originalReplaceState(...args);
    postPreviewLocation();
  };

  window.addEventListener('popstate', () => {
    postPreviewLocation();
  });

  window.addEventListener('hashchange', () => {
    postPreviewLocation();
  });
}

/**
 * Get allowed parent origins based on environment
 * Security: Only accept messages from these trusted origins
 */
function getAllowedOrigins() {
  return [
    'http://localhost',      // Local dev server (any port)
    'https://eventfoundry.app' // Production
  ];
}

/**
 * Validate that postMessage came from allowed origin
 * @param {string} origin - The origin from the message event
 * @returns {boolean} True if origin is allowed
 */
function isOriginAllowed(origin) {
  const allowed = getAllowedOrigins();

  // Check for exact match or localhost with any port
  return allowed.some(allowedOrigin => {
    if (allowedOrigin === 'http://localhost') {
      return origin.startsWith('http://localhost');
    }
    return origin === allowedOrigin;
  });
}

/**
 * Check if we're running in an iframe (potential editor mode)
 * @returns {boolean} True if running in an iframe
 */
export function isInIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access window.top due to CORS, we're definitely in an iframe
    return true;
  }
}

/**
 * Initialize editor mode detection
 * Sends IFRAME_READY to parent and listens for ENTER_EDIT_MODE
 * @param {Function} onReload - Callback function to reload event definitions
 */
export function initEditorMode(onReload) {
  reloadCallback = onReload;
  // Only relevant if we're in an iframe
  if (!isInIframe()) {
    return;
  }

  console.log('EventFoundry: Running in iframe, waiting for editor activation');

  // Listen for messages from parent window
  window.addEventListener('message', (event) => {
    // Get message data directly (no unwrapping needed when parent uses '*' target origin)
    const messageData = event.data;

    // Check if this is an EventFoundry message by looking at the type
    const messageType = messageData?.type;
    const validEventFoundryTypes = [
      'ENTER_EDIT_MODE',
      'ENABLE_PREVIEW',
      'DISABLE_PREVIEW',
      'RELOAD_CONFIG',
      'HIGHLIGHT_SCOPE',
      'CLEAR_HIGHLIGHT',
      'PREVIEW_NAVIGATE',
      'ENTER_PARAM_CAPTURE_MODE',
      'EXIT_PARAM_CAPTURE_MODE'
    ];

    // If it's not an EventFoundry message, silently ignore it
    // This filters out noise from browser extensions, Shopify scripts, etc.
    if (!messageType || !validEventFoundryTypes.includes(messageType)) {
      return; // Not our message, ignore silently
    }

    // Validate the origin
    if (!isOriginAllowed(event.origin)) {
      console.warn('EventFoundry: Rejected postMessage from untrusted origin:', event.origin);
      return;
    }

    // Store the verified parent origin on first valid message
    if (!getParentOrigin()) {
      setParentOrigin(event.origin);
      console.log('EventFoundry: Parent origin established:', event.origin);
      postPreviewLocation();
    }

    switch(messageData.type) {
      case 'ENTER_EDIT_MODE':
        console.log('← ENTER_EDIT_MODE received');
        activateEditorMode();
        break;

      case 'ENABLE_PREVIEW':
        console.log('← ENABLE_PREVIEW received');
        enablePreviewMode();
        break;

      case 'DISABLE_PREVIEW':
        console.log('← DISABLE_PREVIEW received');
        disablePreviewMode();
        break;

      case 'RELOAD_CONFIG':
        console.log('← RELOAD_CONFIG received');
        if (reloadCallback) {
          reloadCallback();
        }
        break;

      case 'PREVIEW_NAVIGATE':
        if (messageData.direction === 'back') {
          window.history.back();
        } else if (messageData.direction === 'forward') {
          window.history.forward();
        }
        break;

      case 'ENTER_PARAM_CAPTURE_MODE':
        console.log('← ENTER_PARAM_CAPTURE_MODE received');
        enterParamCaptureMode(getParentOrigin() ?? '*');
        break;

      case 'EXIT_PARAM_CAPTURE_MODE':
        console.log('← EXIT_PARAM_CAPTURE_MODE received');
        exitParamCaptureMode();
        break;
    }
  });

  installPreviewLocationTracking();

  // Send IFRAME_READY to parent
  // Use a small delay to ensure parent's message listener is ready
  setTimeout(() => {
    if (window.parent) {
      // NOTE: Using '*' here is intentional - we don't know the parent origin yet.
      // The parent will validate this message's origin, and we'll learn the parent's
      // origin when they send ENTER_EDIT_MODE back to us.
      window.parent.postMessage({
        type: 'IFRAME_READY'
      }, '*');
      console.log('→ IFRAME_READY sent to parent');
    }
  }, 100);
}

/**
 * Activate editor mode
 * Creates the visual overlay and enables element selection
 */
function activateEditorMode() {
  // Create overlay and enable element selection
  createEditorOverlay();
}

/**
 * Enable Preview Mode (allow all page interactions)
 */
function enablePreviewMode() {
  console.log('EventFoundry: Enabling Preview Mode');
  enablePreviewOverlay(); // Call overlay function
  postPreviewLocation();
}

/**
 * Disable Preview Mode (return to Edit Mode)
 */
function disablePreviewMode() {
  console.log('EventFoundry: Disabling Preview Mode (Edit Mode active)');
  disablePreviewOverlay(); // Call overlay function
}
